import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';

import {
  AccessibilityWrapper,
  ScrollableContainer,
  Icon,
} from 'legacy-packages/ft-cb';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import './styles.scss';

/**
 * PopOver component for average points on an assignment's part details.
 * */
export const AveragePopOver = (props) => {
  const {
    resultsPathname,
    label,
    actualPoints,
    totalPoints,
    users,
    questions,
  } = props;

  return (
    <div className="AveragePopOver d-flex flex-column justify-center">
      <PopOver
        showCloseButton
        className="average-popover"
        buttonClassName="w-full h-full flex justify-center items-center cursor-pointer"
        accessibility={{
          'aria-label': label,
          role: 'button',
          // Test ID for testing purposes
          'data-test-tags-average-popover': '',
        }}
        trigger={<div data-test-popover-trigger>{label}</div>}
      >
        <div data-test-popover-title className="average-popover-title pt-14">
          On average, students earned {actualPoints} of {totalPoints} total
          points:
        </div>
        <ScrollableContainer autoHeight autoHeightMax={240}>
          <Table data-test-resources-table className="average-popover-table">
            <TableHead className="average-popover-table-header">
              <TableRow
                className="average-popover-table-row"
                data-test-resources-table-columns
              >
                <TableCell className="cell" />
                {questions.map((question) => {
                  const { id, index: questionIndex } = question;
                  const questionRoute = `${resultsPathname}/questions/${id}`;

                  return (
                    <TableCell
                      key={`questions-${questionIndex}`}
                      className="cell question-index"
                    >
                      <AccessibilityWrapper>
                        <Link to={questionRoute} role="button">
                          Q{Number.parseInt(questionIndex, 10) + 1}
                        </Link>
                      </AccessibilityWrapper>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody
              className="average-popover-table-body"
              data-test-table-body
            >
              {users.map((user) => {
                const { id, firstName, lastName, reports } = user;
                const userRoute = `${resultsPathname}/students/${id}`;

                return (
                  <TableRow
                    className="average-popover-table-row"
                    key={`resource-item-key-${id}`}
                  >
                    <TableCell className="cell user-name-cell">
                      <AccessibilityWrapper>
                        <Link to={userRoute} role="button">
                          {firstName} {lastName}
                        </Link>
                      </AccessibilityWrapper>
                    </TableCell>
                    {(reports &&
                      reports.map((report) => {
                        const { itemKey, isAnswered, isCorrect } = report;

                        return (
                          <TableCell
                            key={`question-score-${itemKey}`}
                            className="cell icon-cell"
                          >
                            {(isAnswered &&
                              ((isCorrect && (
                                <Icon
                                  className="correct-answer-icon"
                                  name="correct"
                                  size="1.2em"
                                  title="Correct answer"
                                />
                              )) || (
                                <Icon
                                  className="incorrect-answer-icon"
                                  name="incorrect"
                                  title="Incorrect answer"
                                />
                              ))) || (
                              <div className="no-answer-icon">&mdash;</div>
                            )}
                          </TableCell>
                        );
                      })) || (
                      <TableCell
                        key={`no-results-${id}`}
                        className="cell no-results"
                        colSpan={questions.length}
                      >
                        <div>No results</div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollableContainer>
      </PopOver>
    </div>
  );
};

AveragePopOver.displayName = 'AveragePopOver';

AveragePopOver.propTypes = {
  // The label triggering the popover.
  label: PropTypes.string.isRequired,
  // The points scored by the student.
  actualPoints: PropTypes.string.isRequired,
  // The total points available.
  totalPoints: PropTypes.string.isRequired,
  // The list of users to display
  users: PropTypes.array.isRequired,
  // The list of tag's questions.
  questions: PropTypes.array.isRequired,
  // Results pathname for creating questions/users url
  resultsPathname: PropTypes.string.isRequired,
};

export default AveragePopOver;
